import { useMutation, useQueryClient } from '@tanstack/react-query';
import { commitIngest, IngestCommitAction } from 'src/api/commitIngest';
import { EProductLibraryStatus, IStatus } from 'src/api/getProductLibrarySyncStatus';
import { NotificationType } from 'src/api/getNotifications/getNotifications';
import { useUpdateNotification } from 'src/hooks/useUpdateNotification/useUpdateNotification';
import { getIngestDiffQueryKey, NOTIFICATIONS_QUERY, QueryKeys, SYNC_STATUS_QUERY } from '../QueryKeys';

interface ICommitVariables {
  eventId: string;
  action: IngestCommitAction;
}

/** Caches that can hold records the ingest pipeline has just written. */
const APPROVE_INVALIDATION_KEYS = [
  QueryKeys.items,
  QueryKeys.optionGroups,
  QueryKeys.productSizes,
  QueryKeys.priceSchemes,
];

/** Approves or rejects an ingest event. Both actions hit the same endpoint. */
export const useCommitIngest = () => {
  const queryClient = useQueryClient();
  const { markNotificationRead } = useUpdateNotification();

  const mutation = useMutation({
    mutationFn: ({ eventId, action }: ICommitVariables) => commitIngest({ eventId, action }),
    onMutate: async ({ action }) => {
      await queryClient.cancelQueries({ queryKey: SYNC_STATUS_QUERY });

      const previousStatus = queryClient.getQueryData<IStatus>(SYNC_STATUS_QUERY);
      // Skipped with nothing cached: a status object built from this one field alone would read as
      // a complete IStatus to every consumer. onSettled refetches the real one either way.
      queryClient.setQueryData(SYNC_STATUS_QUERY, (old?: IStatus) =>
        old
          ? {
              ...old,
              sync_status: action === 'approve' ? EProductLibraryStatus.PENDING : EProductLibraryStatus.REJECTED,
            }
          : old,
      );

      return { previousStatus };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousStatus) {
        queryClient.setQueryData(SYNC_STATUS_QUERY, context.previousStatus);
      }
    },
    onSuccess: (_data, { eventId, action }) => {
      // The diff is deleted server-side once actioned, and it can be large.
      queryClient.removeQueries({ queryKey: getIngestDiffQueryKey(eventId) });

      markNotificationRead(
        (notification) => notification.type === NotificationType.PRODUCT_SYNC && notification.event_id === eventId,
      );
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY });

      if (action === 'approve') {
        APPROVE_INVALIDATION_KEYS.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SYNC_STATUS_QUERY });
    },
  });

  return {
    approveSync: (eventId: string) => mutation.mutateAsync({ eventId, action: 'approve' }),
    rejectSync: (eventId: string) => mutation.mutateAsync({ eventId, action: 'reject' }),
    isCommitting: mutation.isLoading,
  };
};
